import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchHomepageData,
  loadHomepageCacheSync,
  persistHomepagePayload,
} from '../api/homepageDataService';
import { subscribeUserDashboard } from '../api/userRealtimeSync';
import { auth } from '../auth/firebaseAuth';
import {
  fetchAllGamesForCache,
  gamesForSportTab,
  loadCachedGamesSync,
  persistGamesCache,
  upcomingUtcDates,
  UPCOMING_WINDOW_DAYS,
} from '../api/gamesService';
import { filterLockableUpcomingGames } from '../utils/gameLockability';
import { saveCachedProfile } from '../utils/profileCache';
import { cacheAgeLabel } from '../utils/dataCache';
import {
  hydrateHomepageProgressively,
  mergeHomepageSections,
  mergePrioritySection,
  mergeSecondarySection,
  renderCachedState,
  splitHomepageSections,
} from '../utils/homepageHydration';
import { perfTimeStart, perfTimeEnd } from '../utils/homepageHydrationPerf';
import { perfTimeline } from '../utils/startupPerf';
import { BG_SYNC_EVENT } from '../pwa/backgroundSync';

const GAMES_REFRESH_MS = 45_000;

const INITIAL_CACHED = renderCachedState();

/** Guest sessions never hydrate from lockedin_homepage_v1. */
const GUEST_HOMEPAGE_SECTIONS = {
  priority: { wallet: null, subscription: null },
  primary: { upcomingGames: [], gamesMeta: null },
  secondary: { profile: null, serverTime: null, cacheHints: null },
};

/**
 * Dashboard data flow:
 * 1. renderCachedState (instant)
 * 2. hydrate via get_homepage_data (one API)
 * 3. Firestore listener on users/{uid} (wallet, subscription, profile — live)
 * 4. periodic games-only refresh (Firestore /games, no user refetch)
 */
export function useHomepageData({ enabled = true, uid = null } = {}) {
  const [priority, setPriority] = useState(
    () => (uid
      ? (INITIAL_CACHED.sections?.priority ?? GUEST_HOMEPAGE_SECTIONS.priority)
      : GUEST_HOMEPAGE_SECTIONS.priority)
  );
  const [primary, setPrimary] = useState(
    () => (uid
      ? (INITIAL_CACHED.sections?.primary ?? GUEST_HOMEPAGE_SECTIONS.primary)
      : GUEST_HOMEPAGE_SECTIONS.primary)
  );
  const [secondary, setSecondary] = useState(
    () => (uid
      ? (INITIAL_CACHED.sections?.secondary ?? GUEST_HOMEPAGE_SECTIONS.secondary)
      : GUEST_HOMEPAGE_SECTIONS.secondary)
  );
  const [hydrationPhase, setHydrationPhase] = useState(
    () => (uid && INITIAL_CACHED.fromCache ? 'cached' : 'idle')
  );
  const [realtimeReady, setRealtimeReady] = useState(false);
  const [error, setError] = useState(null);
  const inflightRef = useRef(false);
  const sectionsRef = useRef({ priority, primary, secondary });

  sectionsRef.current = { priority, primary, secondary };

  const mergedPayload = useMemo(
    () => mergeHomepageSections({ priority, primary, secondary }),
    [priority, primary, secondary]
  );

  const allGames = primary.upcomingGames ?? [];
  const lastUpdated = primary.gamesMeta?.lastUpdated ?? secondary.serverTime ?? null;
  const lookupGames = useMemo(() => {
    const { games } = loadCachedGamesSync();
    return games.length ? games : allGames;
  }, [allGames, lastUpdated]);

  const showWalletSkeleton =
    enabled
    && Boolean(uid)
    && !priority?.wallet
    && !realtimeReady
    && hydrationPhase !== 'cached';

  const showGamesSkeleton =
    enabled
    && allGames.length === 0
    && (hydrationPhase === 'idle' || hydrationPhase === 'fetching' || hydrationPhase === 'priority');

  const isRefreshing = hydrationPhase === 'fetching';

  const applySections = useCallback((sections, phase) => {
    if (!sections) return;
    setPriority(sections.priority);
    setPrimary(sections.primary);
    setSecondary(sections.secondary);
    if (phase) setHydrationPhase(phase);
  }, []);

  const resetToGuestHomepageState = useCallback(() => {
    setPriority(GUEST_HOMEPAGE_SECTIONS.priority);
    setPrimary(GUEST_HOMEPAGE_SECTIONS.primary);
    setSecondary(GUEST_HOMEPAGE_SECTIONS.secondary);
    setHydrationPhase('idle');
    setRealtimeReady(false);
    setError(null);
  }, []);

  const applyRealtimeSections = useCallback((sections, { initial } = {}) => {
    if (!sections) return;
    setPriority((prev) => mergePrioritySection(prev, sections.priority));
    setSecondary((prev) => mergeSecondarySection(prev, sections.secondary));
    if (sections.secondary?.profile && uid) {
      saveCachedProfile(uid, sections.secondary.profile);
    }
    setRealtimeReady(true);
    if (!initial) {
      perfTimeline('realtime.userUpdate', 'firestore', {
        balance: sections.priority?.wallet?.balance,
        status: sections.priority?.subscription?.status,
      });
    }
  }, [uid]);

  /** Full bootstrap hydrate (games + initial user snapshot from API). */
  const refresh = useCallback(async () => {
    if (!enabled || inflightRef.current) return null;
    inflightRef.current = true;
    setHydrationPhase('fetching');
    setError(null);

    perfTimeStart('apiFetch');
    try {
      let idToken = null;
      if (uid && auth.currentUser) {
        idToken = await auth.currentUser.getIdToken();
      }
      const raw = await fetchHomepageData(idToken);
      perfTimeEnd('apiFetch');

      const merged = await hydrateHomepageProgressively(raw, {
        onPriority: async (p) => {
          setPriority((prev) => mergePrioritySection(prev, p));
          setHydrationPhase('priority');
        },
        onPrimary: async (p) => {
          setPrimary(p);
          setHydrationPhase('primary');
        },
        onSecondary: async (s) => {
          setSecondary((prev) => mergeSecondarySection(prev, s));
          setHydrationPhase('secondary');
        },
      });

      if (uid) {
        persistHomepagePayload(merged, uid);
      }
      setHydrationPhase('complete');
      perfTimeline('hydration.complete', 'api', {
        games: merged.upcomingGames?.length ?? 0,
        hasProfile: Boolean(merged.profile),
      });
      return merged;
    } catch (e) {
      perfTimeEnd('apiFetch');
      setError(e?.message || 'Failed to load homepage data');
      setHydrationPhase((prev) => (prev === 'fetching' ? 'idle' : prev));
      return null;
    } finally {
      inflightRef.current = false;
    }
  }, [enabled, uid]);

  /** Games-only periodic refresh — reads /games directly; does not refetch wallet/subscription. */
  const refreshGamesOnly = useCallback(async () => {
    if (!enabled) return null;
    try {
      const dates = upcomingUtcDates(UPCOMING_WINDOW_DAYS);
      const allGames = await fetchAllGamesForCache(UPCOMING_WINDOW_DAYS);
      const games = filterLockableUpcomingGames(allGames, { windowDays: UPCOMING_WINDOW_DAYS });
      const lastUpdated = Date.now();
      const nextPrimary = {
        upcomingGames: games,
        gamesMeta: {
          date: dates[0],
          dateTo: dates[dates.length - 1],
          windowDays: UPCOMING_WINDOW_DAYS,
          lastUpdated,
          gameCount: games.length,
          source: 'games_collection',
        },
      };
      setPrimary(nextPrimary);
      persistGamesCache(allGames, lastUpdated);
      const merged = mergeHomepageSections({
        ...sectionsRef.current,
        primary: nextPrimary,
      });
      if (uid) {
        persistHomepagePayload(merged, uid);
      }
      perfTimeline('refresh.gamesOnly', 'firestore', { games: games.length });
      return nextPrimary;
    } catch (e) {
      console.warn('refreshGamesOnly:', e);
      return null;
    }
  }, [enabled, uid]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const refreshGamesRef = useRef(refreshGamesOnly);
  refreshGamesRef.current = refreshGamesOnly;

  // Background sync — refresh games cache when SW signals idle (no scrapers).
  useEffect(() => {
    if (!enabled) return undefined;
    const onBgSync = () => refreshGamesRef.current();
    window.addEventListener(BG_SYNC_EVENT, onBgSync);
    return () => window.removeEventListener(BG_SYNC_EVENT, onBgSync);
  }, [enabled]);

  // Bootstrap: cache → full API hydrate → games-only interval (no user polling).
  // Guest (uid null): never read homepage cache; fresh unauthenticated fetch only.
  useEffect(() => {
    if (!enabled) return undefined;

    if (!uid) {
      resetToGuestHomepageState();
      refreshRef.current();
      // Seed the full games cache (live + final rows) immediately so the Live
      // Now strip isn't empty until the first interval tick.
      refreshGamesRef.current();
      const interval = window.setInterval(() => refreshGamesRef.current(), GAMES_REFRESH_MS);
      return () => window.clearInterval(interval);
    }

    const local = loadHomepageCacheSync();
    if (local) {
      applySections(splitHomepageSections(local), 'cached');
    }

    refreshRef.current();
    refreshGamesRef.current();
    const interval = window.setInterval(() => refreshGamesRef.current(), GAMES_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, [enabled, uid, applySections, resetToGuestHomepageState]);

  // Real-time: ONE listener per uid — wallet, subscription, profile; cleanup on logout/unmount.
  useEffect(() => {
    if (!enabled || !uid) {
      setRealtimeReady(false);
      return undefined;
    }

    setRealtimeReady(false);
    perfTimeStart('firestore.userRealtime');
    const unsub = subscribeUserDashboard(uid, (sections, meta) => {
      if (meta.initial) {
        perfTimeEnd('firestore.userRealtime');
        perfTimeline('realtime.userAttached', 'firestore', { uid });
      }
      applyRealtimeSections(sections, meta);
    });

    return () => {
      unsub();
      setRealtimeReady(false);
    };
  }, [enabled, uid, applyRealtimeSections]);

  return {
    payload: mergedPayload,
    priority,
    primary,
    secondary,
    hydrationPhase,
    realtimeReady,
    upcomingGames: allGames,
    gamesMeta: primary.gamesMeta,
    wallet: priority.wallet,
    subscription: priority.subscription,
    profile: secondary.profile,
    allGames,
    lookupGames,
    lastUpdated,
    freshnessLabel: cacheAgeLabel(lastUpdated),
    isRefreshing,
    error,
    refresh,
    refreshGamesOnly,
    gamesForTab: (tab) => gamesForSportTab(allGames, tab),
    showGamesSkeleton,
    showWalletSkeleton,
    hadCache: INITIAL_CACHED.fromCache,
  };
}
